import React, { useState, useMemo } from 'react';
import './TradingActivityFeed.css';

const TradingActivityFeed = ({ data }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'buy', 'sell'
  const [sortBy, setSortBy] = useState('time'); // 'time', 'amount', 'pool'
  const [showDetails, setShowDetails] = useState(new Set());

  // Process and filter trading data
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = [...data];

    // Apply trade type filter
    if (filter !== 'all') {
      filtered = filtered.filter(trade => trade.tradeType === filter);
    }

    // Sort data
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.amountIn - a.amountIn;
        case 'pool':
          return a.poolName.localeCompare(b.poolName);
        case 'time':
        default:
          return new Date(b.executedAt) - new Date(a.executedAt);
      }
    });

    return filtered;
  }, [data, filter, sortBy]);

  // Toggle trade details
  const toggleDetails = (index) => {
    const newShowDetails = new Set(showDetails);
    if (newShowDetails.has(index)) {
      newShowDetails.delete(index);
    } else {
      newShowDetails.add(index);
    }
    setShowDetails(newShowDetails);
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    const now = new Date();
    const tradeTime = new Date(dateStr);
    const diffMs = now - tradeTime;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Format token address (show first 4 and last 4 characters)
  const formatTokenAddress = (address) => {
    if (!address || address.length < 8) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Format amount with appropriate precision
  const formatAmount = (amount, isSOL = false) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '0.00';
    if (numAmount >= 1000) {
      return `${(numAmount / 1000).toFixed(2)}K`;
    }
    if (numAmount >= 1) {
      return numAmount.toFixed(isSOL ? 3 : 2);
    }
    return numAmount.toFixed(6);
  };

  // Get risk level color
  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low': return '#10B981';
      case 'medium': return '#F59E0B';
      case 'high': return '#EF4444';
      default: return '#6B7280';
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="trading-activity-feed empty">
        <div className="empty-state">
          <h4>No Trading Activity</h4>
          <p>Recent pool trading activity will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trading-activity-feed">
      <div className="feed-controls">
        <div className="filter-controls">
          <label>Filter by type:</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Trades</option>
            <option value="buy">Buy Orders</option>
            <option value="sell">Sell Orders</option>
          </select>
        </div>

        <div className="sort-controls">
          <label>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="time">Recent First</option>
            <option value="amount">Amount (High to Low)</option>
            <option value="pool">Pool Name</option>
          </select>
        </div>

        <div className="feed-stats">
          <span className="stat">
            {processedData.length} trades
          </span>
          <span className="stat">
            {processedData.filter(t => t.tradeType === 'buy').length} buys
          </span>
          <span className="stat">
            {processedData.filter(t => t.tradeType === 'sell').length} sells
          </span>
        </div>
      </div>

      <div className="activity-list">
        {processedData.map((trade, index) => (
          <div 
            key={index} 
            className={`activity-item ${trade.tradeType}`}
            onClick={() => toggleDetails(index)}
          >
            <div className="activity-main">
              <div className="trade-indicator">
                <div className={`trade-type-badge ${trade.tradeType}`}>
                  {trade.tradeType === 'buy' ? '📈' : '📉'}
                  <span>{trade.tradeType.toUpperCase()}</span>
                </div>
              </div>

              <div className="trade-details">
                <div className="trade-primary">
                  <span className="trade-amount">
                    {formatAmount(trade.amountIn, true)} SOL
                  </span>
                  <span className="trade-arrow">→</span>
                  <span className="token-info">
                    {formatTokenAddress(trade.tokenAddress)}
                  </span>
                </div>
                
                <div className="trade-secondary">
                  <span className="pool-name">{trade.poolName}</span>
                  <span 
                    className="pool-strategy"
                    style={{ color: getRiskColor(trade.riskLevel) }}
                  >
                    {trade.poolStrategy} • {trade.riskLevel} risk
                  </span>
                </div>
              </div>

              <div className="trade-meta">
                <div className="trade-time">
                  {formatTimeAgo(trade.executedAt)}
                </div>
                <div className="trade-price">
                  ${parseFloat(trade.price || 0).toFixed(6)}
                </div>
              </div>

              <div className="expand-indicator">
                {showDetails.has(index) ? '▼' : '▶'}
              </div>
            </div>

            {showDetails.has(index) && (
              <div className="activity-details">
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Token Address:</span>
                    <span className="detail-value monospace">
                      {trade.tokenAddress}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Amount In:</span>
                    <span className="detail-value">
                      {parseFloat(trade.amountIn || 0).toFixed(6)} SOL
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Amount Out:</span>
                    <span className="detail-value">
                      {parseFloat(trade.amountOut || 0).toFixed(6)} tokens
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Price per Token:</span>
                    <span className="detail-value">
                      ${parseFloat(trade.price || 0).toFixed(8)}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Pool Strategy:</span>
                    <span className="detail-value">
                      {trade.poolStrategy}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Risk Level:</span>
                    <span 
                      className="detail-value"
                      style={{ color: getRiskColor(trade.riskLevel) }}
                    >
                      {trade.riskLevel}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Executed At:</span>
                    <span className="detail-value">
                      {new Date(trade.executedAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {processedData.length === 0 && filter !== 'all' && (
        <div className="no-results">
          <p>No {filter} trades found. Try adjusting your filters.</p>
        </div>
      )}

      <div className="feed-footer">
        <div className="activity-summary">
          <div className="summary-item">
            <span className="summary-label">Total Volume (24h):</span>
            <span className="summary-value">
              {formatAmount(
                processedData
                  .filter(trade => {
                    const tradeTime = new Date(trade.executedAt);
                    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                    return tradeTime > dayAgo;
                  })
                  .reduce((sum, trade) => sum + trade.amountIn, 0),
                true
              )} SOL
            </span>
          </div>
          
          <div className="summary-item">
            <span className="summary-label">Unique Tokens:</span>
            <span className="summary-value">
              {new Set(processedData.map(trade => trade.tokenAddress)).size}
            </span>
          </div>
          
          <div className="summary-item">
            <span className="summary-label">Active Pools:</span>
            <span className="summary-value">
              {new Set(processedData.map(trade => trade.poolName)).size}
            </span>
          </div>
        </div>
        
        <div className="feed-disclaimer">
          <p>
            <small>
              Trading data is anonymized for privacy. Times shown in your local timezone.
            </small>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TradingActivityFeed;